import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { closureFor, fmtLong } from "./calendarUtils";

// lesson presente = spostamento, lesson null = nuovo recupero (courses: scelta del corso dalla tab Calendario)
export const LessonDialog = ({ open, onOpenChange, lesson, closures, onSubmit, courses, defaultCourse }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [course, setCourse] = useState("");
  const [busy, setBusy] = useState(false);
  const isMove = !!lesson;

  useEffect(() => {
    if (open) { setDate(lesson?.date || ""); setTime(lesson?.start_time || ""); setCourse(defaultCourse || ""); }
  }, [open, lesson, defaultCourse]);
  const closure = date ? closureFor(date, closures) : null;
  const changed = isMove && (date !== lesson.date || time !== lesson.start_time);
  const needCourse = !isMove && !!courses;

  const submit = async () => {
    setBusy(true);
    try { await onSubmit(needCourse ? { date, start_time: time, course } : { date, start_time: time }); onOpenChange(false); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lesson-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isMove ? "Sposta lezione" : "Aggiungi lezione di recupero"}</DialogTitle></DialogHeader>
        {isMove && <p className="text-sm text-muted-foreground" data-testid="lesson-dialog-current">Attuale: <span className="text-foreground">{fmtLong(lesson.date)}</span> · {lesson.start_time}</p>}
        {needCourse && (
          <div><Label className="text-xs">Corso</Label>
            <Select value={course} onValueChange={setCourse}>
              <SelectTrigger data-testid="lesson-dialog-course" className="mt-1 bg-white h-auto min-h-10 text-left whitespace-normal"><SelectValue placeholder="Scegli il corso" /></SelectTrigger>
              <SelectContent>{courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-xs">Nuova data</Label>
            <Input data-testid="lesson-dialog-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 bg-white" />
            <p data-testid="lesson-dialog-weekday" className="mt-1.5 text-base font-medium text-primary first-letter:uppercase min-h-[1.5rem]">{fmtLong(date) || <span className="text-sm font-normal text-muted-foreground">Scegli una data</span>}</p>
          </div>
          <div className="col-span-2 sm:col-span-1"><Label className="text-xs">Ora di inizio</Label><Input data-testid="lesson-dialog-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 bg-white" /></div>
        </div>
        {date && (
          <div data-testid="lesson-dialog-summary" className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Riepilogo</p>
            {isMove ? (
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>da <strong>{fmtLong(lesson.date)}</strong> {lesson.start_time}</span>
                <ArrowRight size={14} className="text-muted-foreground" />
                <span>a <strong>{fmtLong(date)}</strong> {time}</span>
              </p>
            ) : <p>Recupero{needCourse && course ? <> di <strong>{course}</strong></> : ""} <strong>{fmtLong(date)}</strong>{time ? ` alle ${time}` : ""}</p>}
            {isMove && !changed && <p className="text-xs text-muted-foreground">Data e orario non ancora cambiati.</p>}
          </div>
        )}
        {closure && (
          <div data-testid="lesson-dialog-closure-warning" className="flex gap-2 rounded-lg bg-amber-500/15 text-amber-900 p-3 text-sm">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span><strong className="first-letter:uppercase inline-block">{fmtLong(date)}</strong> la scuola è chiusa{closure.description ? ` (${closure.description})` : ""}. Puoi procedere comunque.</span>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" data-testid="lesson-dialog-cancel" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="lesson-dialog-submit" disabled={!date || !time || busy || (needCourse && !course)} onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {closure ? "Conferma comunque" : isMove ? "Sposta" : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
